import { datadogRum } from '@datadog/browser-rum'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'

import { CaughtErrorPage, LayoutErrorPage, NotFoundErrorPage } from '@src/components/error-handling/ErrorComponents'
import { PAGE_TYPES } from '@src/constants/page-type'
import { isMobileDevice } from '@src/quince-site-common/src/helpers/getDeviceTypeForPageRouter'
import { GetSeoMetapageDataProps } from '@src/quince-site-common/src/utils/seo/getSeoMetadata'
import { BundlePageBFFResponse } from '@src/quince-site-pdp/src/domains/BundlePageContext'
import { ProductPageBFFResponse } from '@src/quince-site-pdp/src/domains/ProductPageContext'
import { getServerSideBaseProps } from '@src/server-side/get-server-side-props'
import { CATCH_ALL_IGNORE_PREFIXES, SERVER_SIDE_RENDER_TIME_HEADER, ServerSideProps } from '@src/utils/page-type.utils'

import Layout from '../layout'

import Collection from './components/collection/domains/Collection'
import CollectionPageTemplate from './page-templates/collection-page-template'
import FaqPageTemplate from './page-templates/faq-page-template'
import LandingPageTemplate from './page-templates/landing-page-template'
import PartnerPDPPageTemplate from './page-templates/partner-pdp-page-template'
import ProductAndBundlePageTemplate from './page-templates/product-and-bundle-page-template'

const PAGE_TEMPLATES = {
  [PAGE_TYPES.COLLECTION]: CollectionPageTemplate,
  [PAGE_TYPES.SUBCOLLECTION]: CollectionPageTemplate,
  [PAGE_TYPES.PARTNER_PDP]: PartnerPDPPageTemplate,
  [PAGE_TYPES.FAQ]: FaqPageTemplate,
  [PAGE_TYPES.PRODUCT_AND_BUNDLE]: ProductAndBundlePageTemplate,
  [PAGE_TYPES.LANDING]: LandingPageTemplate,
}

type PageTemplateType = keyof typeof PAGE_TEMPLATES

function isValidPageType(pageType: string | undefined): pageType is PageTemplateType {
  return Object.keys(PAGE_TEMPLATES).includes(pageType as PageTemplateType)
}

export const getServerSideProps = (async ctx => {
  const pathName = Array.isArray(ctx.params?.slug) ? ctx.params.slug.join('/') : ''
  if (CATCH_ALL_IGNORE_PREFIXES.some(prefix => pathName.startsWith(prefix))) {
    return {
      props: {
        type: '_ignore_path',
      },
    }
  }
  //add server time to all SSR requests
  const start = performance.now()

  const serverSideBaseProps = await getServerSideBaseProps(ctx, pathName)
  const end = performance.now()
  const duration = end - start
  ctx.res.setHeader(SERVER_SIDE_RENDER_TIME_HEADER, `${duration}ms`)

  if ('props' in serverSideBaseProps) {
    const props = await serverSideBaseProps.props
    return {
      props: {
        type: '_page',
        ...props,
      },
    }
  }

  return serverSideBaseProps
}) satisfies GetServerSideProps<ServerSideProps>

export default function CatchAllPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  if (props.type != '_page') {
    return null
  }

  try {
    if (props.pageData) {
      const ssrPageType = props.pageData.context?.ssrPageType
      if (isValidPageType(ssrPageType)) {
        datadogRum.setViewContextProperty('page_type', ssrPageType)
        const PageTemplate = PAGE_TEMPLATES[ssrPageType]
        const pageData = {
          ...props.pageData,
          context: {
            ...props.pageData.context,
            pageDataJson: (props.pageData.context?.pageDataJson ?? {}) as
              | ProductPageBFFResponse
              | BundlePageBFFResponse
              | Collection,
            seoData: props.seoMetaData as GetSeoMetapageDataProps | null,
          },
        }

        return (
          <Layout {...props} overrideChildrenForNon200={true}>
            <PageTemplate
              pathname={props.pathName}
              pageData={pageData}
              isMobile={isMobileDevice(props.deviceType)}
              serverSideFeatureFlags={props.serverSideFeatureFlags}
              zipCode={props.zipCodeInfo?.zipCode ?? null}
            />
          </Layout>
        )
      }
    } else if (props.siteData) {
      return <LayoutErrorPage props={props} />
    }

    return <NotFoundErrorPage props={props} />
  } catch (err) {
    return <CaughtErrorPage error={err} props={props} />
  }
}
